import { EditorView } from "@codemirror/view";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags } from "@lezer/highlight";

/**
 * Editor theme that adapts to light/dark via CSS custom properties.
 * The `dark: false` flag only affects CodeMirror's internal base styles;
 * all colors use CSS vars so they respond to [data-theme] changes.
 */
const leverEditorTheme = EditorView.theme({
  "&": {
    backgroundColor: "var(--bg-surface)",
    color: "var(--text-primary)",
    fontFamily: "var(--font-mono)",
    fontSize: "13.5px",
    lineHeight: "1.6",
  },

  ".cm-content": {
    caretColor: "var(--text-primary)",
    padding: "12px 0",
    fontFamily: "var(--font-mono)",
  },

  ".cm-cursor, .cm-dropCursor": {
    borderLeftColor: "var(--text-primary)",
    borderLeftWidth: "1.5px",
  },

  "&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection": {
    background: "var(--editor-selection, rgba(255, 92, 0, 0.12))",
  },

  ".cm-activeLine": {
    backgroundColor: "var(--editor-active-line, rgba(0, 0, 0, 0.03))",
  },

  ".cm-gutters": {
    backgroundColor: "var(--bg-surface)",
    color: "var(--text-muted)",
    border: "none",
    borderRight: "1px solid var(--border-hairline)",
    fontFamily: "var(--font-mono)",
    fontSize: "11px",
    minWidth: "36px",
  },

  ".cm-activeLineGutter": {
    backgroundColor: "transparent",
    color: "var(--text-secondary)",
  },

  ".cm-lineNumbers .cm-gutterElement": {
    padding: "0 6px 0 4px",
  },

  ".cm-foldPlaceholder": {
    backgroundColor: "var(--bg-surface-hover)",
    border: "1px solid var(--border-hairline)",
    borderRadius: "var(--radius-sm)",
    color: "var(--text-muted)",
    padding: "0 4px",
  },

  ".cm-tooltip": {
    backgroundColor: "var(--bg-surface)",
    border: "1px solid var(--border-subtle)",
    borderRadius: "var(--radius-md)",
    boxShadow: "var(--shadow-lg)",
    color: "var(--text-primary)",
    maxHeight: "260px",
    maxWidth: "400px",
    overflow: "auto",
    zIndex: "70",
  },

  ".cm-tooltip-autocomplete": {
    maxHeight: "220px",
    "& > ul": {
      fontFamily: "var(--font-mono)",
      fontSize: "12px",
      maxHeight: "200px",
      overflowY: "auto",
    },
    "& > ul > li": {
      padding: "3px 8px",
    },
    "& > ul > li[aria-selected]": {
      backgroundColor: "var(--bg-surface-hover)",
      color: "var(--text-primary)",
    },
  },

  ".cm-tooltip-hover": {
    maxWidth: "360px",
    maxHeight: "200px",
    overflowY: "auto",
    overflowWrap: "break-word",
    wordBreak: "break-word",
  },

  ".cm-panels": {
    backgroundColor: "var(--bg-surface)",
    borderColor: "var(--border-hairline)",
    color: "var(--text-primary)",
  },

  ".cm-panels.cm-panels-top": {
    borderBottom: "1px solid var(--border-hairline)",
  },

  ".cm-panels.cm-panels-bottom": {
    borderTop: "1px solid var(--border-hairline)",
  },

  ".cm-panel.cm-search": {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "4px 6px",
    padding: "6px 8px",
    fontFamily: "var(--font-sans)",
    fontSize: "12px",
  },

  ".cm-panel.cm-search br": {
    display: "none",
  },

  ".cm-panel.cm-search label": {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    color: "var(--text-secondary)",
    cursor: "pointer",
    userSelect: "none",
  },

  ".cm-panel input[type='text'], .cm-panel input[type='search']": {
    fontFamily: "var(--font-mono)",
    fontSize: "12px",
    color: "var(--text-primary)",
    backgroundColor: "var(--bg-surface-hover)",
    border: "1px solid var(--border-subtle)",
    borderRadius: "var(--radius-sm)",
    padding: "3px 8px",
    height: "26px",
    outline: "none",
    minWidth: "120px",
  },

  ".cm-panel input[type='text']:focus, .cm-panel input[type='search']:focus": {
    borderColor: "var(--color-lever-orange)",
    boxShadow: "0 0 0 1px var(--color-lever-orange)",
  },

  ".cm-panel input[type='checkbox']": {
    appearance: "auto",
    WebkitAppearance: "auto",
    MozAppearance: "auto",
    width: "13px",
    height: "13px",
    cursor: "pointer",
    accentColor: "var(--color-lever-orange)",
  },

  ".cm-panel button": {
    fontFamily: "var(--font-sans)",
    fontSize: "11px",
    fontWeight: "500",
    color: "var(--text-secondary)",
    backgroundColor: "var(--bg-surface-hover)",
    border: "1px solid var(--border-subtle)",
    borderRadius: "var(--radius-sm)",
    padding: "3px 10px",
    height: "26px",
    cursor: "pointer",
    transition: "all 100ms ease",
    whiteSpace: "nowrap",
  },

  ".cm-panel button:hover": {
    color: "var(--text-primary)",
    backgroundColor: "var(--bg-secondary)",
    borderColor: "var(--border-default)",
  },

  ".cm-panel button:active": {
    transform: "scale(0.97)",
  },

  ".cm-panel button[name='close']": {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "22px",
    height: "22px",
    padding: "0",
    borderRadius: "var(--radius-sm)",
    color: "var(--text-muted)",
    backgroundColor: "transparent",
    border: "none",
    marginLeft: "auto",
  },

  ".cm-panel button[name='close']:hover": {
    color: "var(--text-primary)",
    backgroundColor: "var(--bg-surface-hover)",
  },

  ".cm-searchMatch": {
    backgroundColor: "var(--editor-search-match, rgba(251, 146, 60, 0.2))",
    borderRadius: "2px",
  },

  ".cm-searchMatch.cm-searchMatch-selected": {
    backgroundColor: "var(--editor-search-selected, rgba(251, 146, 60, 0.4))",
  },

  ".cm-matchingBracket": {
    backgroundColor: "var(--editor-bracket-match, rgba(255, 92, 0, 0.1))",
    outline: "1px solid var(--border-subtle)",
    borderRadius: "2px",
  },

  ".cm-selectionMatch": {
    backgroundColor: "var(--editor-selection-match, rgba(255, 92, 0, 0.06))",
  },

  "&.cm-focused": {
    outline: "none",
  },

  ".cm-scroller": {
    overflow: "auto",
    fontFamily: "var(--font-mono)",
  },

  ".cm-line": {
    padding: "0 2px 0 8px",
  },

  ".lever-comment-line": {
    backgroundColor: "var(--editor-comment-bg)",
  },

  ".lever-comment-line-focused": {
    backgroundColor: "var(--editor-comment-focused-bg)",
  },

  ".lever-comment-gutter": {
    width: "14px",
    minWidth: "14px",
  },
});

const leverHighlightStyle = HighlightStyle.define([
  { tag: tags.keyword, color: "var(--syntax-command)", fontWeight: "500" },
  { tag: tags.controlKeyword, color: "var(--syntax-command)" },
  { tag: tags.operatorKeyword, color: "var(--syntax-command)" },
  { tag: tags.definitionKeyword, color: "var(--syntax-command)" },

  { tag: tags.string, color: "var(--syntax-string)" },
  { tag: tags.special(tags.string), color: "var(--syntax-string)" },

  { tag: tags.comment, color: "var(--syntax-comment)", fontStyle: "italic" },
  { tag: tags.lineComment, color: "var(--syntax-comment)", fontStyle: "italic" },
  { tag: tags.blockComment, color: "var(--syntax-comment)", fontStyle: "italic" },

  { tag: tags.number, color: "var(--syntax-number)" },
  { tag: tags.integer, color: "var(--syntax-number)" },
  { tag: tags.float, color: "var(--syntax-number)" },

  { tag: tags.invalid, color: "var(--color-soft-red)", textDecoration: "underline wavy" },

  { tag: tags.typeName, color: "var(--syntax-environment)" },
  { tag: tags.className, color: "var(--syntax-environment)" },
  { tag: tags.namespace, color: "var(--syntax-environment)" },

  { tag: tags.function(tags.variableName), color: "var(--syntax-number)" },
  { tag: tags.definition(tags.variableName), color: "var(--text-primary)" },
  { tag: tags.variableName, color: "var(--text-primary)" },

  { tag: tags.atom, color: "var(--syntax-math)" },
  { tag: tags.bool, color: "var(--syntax-command)" },

  { tag: tags.bracket, color: "var(--syntax-bracket)" },
  { tag: tags.angleBracket, color: "var(--syntax-bracket)" },
  { tag: tags.squareBracket, color: "var(--syntax-bracket)" },
  { tag: tags.paren, color: "var(--syntax-bracket)" },
  { tag: tags.brace, color: "var(--syntax-bracket)" },

  { tag: tags.operator, color: "var(--text-secondary)" },
  { tag: tags.punctuation, color: "var(--text-muted)" },

  { tag: tags.heading, color: "var(--syntax-command)", fontWeight: "600" },
  { tag: tags.heading1, color: "var(--syntax-command)", fontWeight: "700", fontSize: "1.2em" },
  { tag: tags.heading2, color: "var(--syntax-command)", fontWeight: "600", fontSize: "1.1em" },
  { tag: tags.heading3, color: "var(--syntax-command)", fontWeight: "600" },

  { tag: tags.emphasis, fontStyle: "italic" },
  { tag: tags.strong, fontWeight: "600" },
  { tag: tags.strikethrough, textDecoration: "line-through" },

  { tag: tags.link, color: "var(--syntax-number)", textDecoration: "underline" },
  { tag: tags.url, color: "var(--syntax-number)" },

  { tag: tags.meta, color: "var(--syntax-environment)" },
  { tag: tags.processingInstruction, color: "var(--syntax-command)" },
]);

export const leverTheme = [
  leverEditorTheme,
  syntaxHighlighting(leverHighlightStyle),
];
